import React, { createContext, useContext, useState, useCallback } from 'react';

const ListPageCacheContext = createContext();

export const useListPageCache = () => {
  const context = useContext(ListPageCacheContext);
  if (!context) {
    throw new Error('useListPageCache must be used within ListPageCacheProvider');
  }
  return context;
};

let cacheStore = {};

export const generateCacheKey = (tab, category, transactionCode, masterType, subType) => {
  if (tab === 'master' && masterType && subType) {
    return `master_${masterType}_${subType}`;
  }
  if (transactionCode) {
    return `${tab}_${category}_${transactionCode}`;
  }
  return `${tab}_${category}`;
};

export const getCachedDataDirect = (cacheKey) => {
  const cached = cacheStore[cacheKey];
  if (cached) {
    const cacheAge = Date.now() - cached.timestamp;
    if (cacheAge < 300000) {
      return cached.data;
    }   
    delete cacheStore[cacheKey];
  }
  return null;
};

export const ListPageCacheProvider = ({ children }) => {
  const [cache, setCache] = useState(() => cacheStore);

  React.useEffect(() => {
    cacheStore = cache;
  }, [cache]);

  const getCacheKey = useCallback((tab, category, transactionCode, masterType, subType) => {
    if (tab === 'master' && masterType && subType) {
      return `master_${masterType}_${subType}`;
    }
    if (transactionCode) {
      return `${tab}_${category}_${transactionCode}`;
    }
    return `${tab}_${category}`;
  }, []);

  const getCachedDataSync = useCallback((cacheKey) => {
    const cached = cacheStore[cacheKey];
    if (cached) {
      const cacheAge = Date.now() - cached.timestamp;
      if (cacheAge < 300000) {
        return cached.data;
      }
      delete cacheStore[cacheKey];
      setCache((prev) => {
        const newCache = { ...prev };
        delete newCache[cacheKey];
        return newCache;
      });
    }
    return null;
  }, []);

  const getCachedData = useCallback((cacheKey) => {
    const cached = cache[cacheKey];
    if (cached) {
      const cacheAge = Date.now() - cached.timestamp;
      if (cacheAge < 300000) {
        return cached.data;
      }
      setCache((prev) => {
        const newCache = { ...prev };
        delete newCache[cacheKey];
        return newCache;
      });
    }
    return null;
  }, [cache]);

  const setCachedData = useCallback((cacheKey, data) => {
    const cacheEntry = {
      data,
      timestamp: Date.now(),
    };
        cacheStore[cacheKey] = cacheEntry;
    setCache((prev) => ({
      ...prev,
      [cacheKey]: cacheEntry,
    }));
  }, []);

  const clearCache = useCallback((cacheKey) => {
    delete cacheStore[cacheKey];
    setCache((prev) => {
      const newCache = { ...prev };
      delete newCache[cacheKey];
      return newCache;
    });
  }, []);
                    
  const clearAllCache = useCallback(() => {
    cacheStore = {};
    setCache({});
  }, []);

  const clearCacheForTab = useCallback((tab) => {
    if (!tab) return;
    const prefix = `${tab}_`;
    const keysToDelete = Object.keys(cacheStore).filter((key) =>
      key.startsWith(prefix)
    );
    keysToDelete.forEach((key) => delete cacheStore[key]);
    setCache((prev) => {
      const newCache = { ...prev };
      keysToDelete.forEach((key) => delete newCache[key]);
      return newCache;
    });
  }, []);

  const value = {
    getCacheKey,
    getCachedData,
    getCachedDataSync,
    setCachedData,
    clearCache,
    clearAllCache,
    clearCacheForTab,
  };

  return (
    <ListPageCacheContext.Provider value={value}>
      {children}
    </ListPageCacheContext.Provider>
  );
};

